import first from 'lodash/first';
import get from 'lodash/get';

const state = {
  adventure: {},
  selectedDeparture: null,
  selectedDepartureQuantity: 1,
  extras: [],
  addons: [],
  addon_extras: [],
  loading: false,
  error: null,
  peopleCount: 1,
};

const getters = {
  priceGbp: (state) => {
    if (state.selectedDeparture) {
      let extrasCost = state.extras.reduce((acc, extra) => {
        return acc + get(extra, 'price_gbp', 0) * extra.quantity;
      }, 0);

      let addonsCost = state.addons.reduce((acc, addon) => {
        return acc + get(addon, 'price_gbp', 0) * addon.quantity;
      }, 0);

      let addonExtrasCost = state.addon_extras.reduce((acc, addon_extra) => {
        return acc + get(addon_extra, 'price_gbp', 0) * addon_extra.quantity;
      }, 0);

      return (
        state.selectedDeparture.price_gbp * state.peopleCount +
        extrasCost +
        addonsCost +
        addonExtrasCost
      );
    } else if (state.adventure) {
      return state.adventure.min_price_gbp * state.peopleCount;
    } else {
      return 0;
    }
  },

  priceUsd: (state) => {
    if (state.selectedDeparture) {
      let extrasCost = state.extras.reduce((acc, extra) => {
        return acc + get(extra, 'price_usd', 0) * extra.quantity;
      }, 0);

      let addonsCost = state.addons.reduce((acc, addon) => {
        return acc + get(addon, 'price_usd', 0) * addon.quantity;
      }, 0);

      let addonExtrasCost = state.addon_extras.reduce((acc, addon_extra) => {
        return acc + get(addon_extra, 'price_usd', 0) * addon_extra.quantity;
      }, 0);

      return (
        state.selectedDeparture.price_usd * state.peopleCount +
        extrasCost +
        addonsCost +
        addonExtrasCost
      );
    } else if (state.adventure) {
      return state.adventure.min_price_usd * state.peopleCount;
    } else {
      return 0;
    }
  },
};

const mutations = {
  UPDATE_SELECTED_DEPARTURE(state, id) {
    if (id) {
      state.selectedDeparture = state.adventure.departures.find(
        (departure) => departure.uuid === id
      );
    } else {
      state.selectedDeparture = null;
    }
  },

  UPDATE_SELECTED_DEPARTURE_QUANTITY(state, quantity) {
    state.quantity = quantity;
  },

  TOGGLE_SELECT_EXTRA(state, extra) {
    let exists =
      state.extras.filter((loop_extra) => {
        return loop_extra.uuid === extra.uuid;
      }).length > 0;

    if (exists) {
      state.extras = state.extras.filter((loop_extra) => {
        return loop_extra.uuid !== extra.uuid;
      });
    } else {
      state.extras.push({
        quantity: 1,
        uuid: extra.uuid,
        price_gbp: extra.price_gbp,
        price_usd: extra.price_usd,
      });
    }
  },

  UPDATE_EXTRA_QUANTITY(state, { quantity, uuid }) {
    let extraIndex = state.extras.findIndex((item) => item.uuid === uuid);

    if (extraIndex === -1) {
      return;
    }

    let extra = state.extras[extraIndex];

    quantity = Math.min(quantity, state.peopleCount);

    let updatedExtra = Object.assign({}, extra, { quantity });

    state.extras = [
      ...state.extras.slice(0, extraIndex),
      updatedExtra,
      ...state.extras.slice(extraIndex + 1, state.extras.length),
    ];
  },

  TOGGLE_SELECT_ADDON(state, addon) {
    let exists =
      state.addons.filter((loop_addon) => {
        return loop_addon.slug === addon.slug;
      }).length > 0;

    if (exists) {
      state.addons = state.addons.filter((loop_addon) => {
        return loop_addon.slug !== addon.slug;
      });

      // Deselect addon-extras for this addon
      addon.extras.forEach((extra) => {
        state.addon_extras = state.addon_extras.filter((addon_extra) => {
          return addon_extra.uuid !== extra.uuid;
        });
      });
    } else {
      state.addons.push({
        quantity: 1,
        slug: addon.slug,
        price_gbp: addon.min_price_gbp,
        price_usd: addon.min_price_usd,
      });
    }
  },

  UPDATE_ADDON_QUANTITY(state, { quantity, slug }) {
    let addonIndex = state.addons.findIndex((item) => item.slug === slug);

    if (addonIndex === -1) {
      return;
    }

    const addon = state.addons[addonIndex];

    quantity = Math.min(quantity, state.peopleCount);

    let updatedAddon = Object.assign({}, addon, { quantity });

    state.addons = [
      ...state.addons.slice(0, addonIndex),
      updatedAddon,
      ...state.addons.slice(addonIndex + 1, state.addons.length),
    ];
  },

  TOGGLE_SELECT_ADDON_EXTRA(state, extra) {
    let selected_addon_uuids = state.addons.map((addon) => addon.slug);

    // Find the selected extra's addon
    let addon = first(
      state.adventure.addons.filter((addon) => {
        return addon.extras.filter((filter_extra) => {
          return filter_extra.uuid === extra.uuid;
        });
      })
    );

    // addon isn't selected
    let addon_selected = selected_addon_uuids.includes(addon.slug);

    // Don't allow selecting addon-extra for addon that isn't selected.
    if (!addon_selected) {
      return;
    }

    let exists =
      state.addon_extras.filter((loop_extra) => {
        return loop_extra.uuid === extra.uuid;
      }).length > 0;

    if (exists) {
      state.addon_extras = state.addon_extras.filter((loop_extra) => {
        return loop_extra.uuid !== extra.uuid;
      });
    } else {
      state.addon_extras.push({
        quantity: 1,
        uuid: extra.uuid,
        price_gbp: extra.price_gbp,
        price_usd: extra.price_usd,
      });
    }
  },

  UPDATE_ADDON_EXTRA_QUANTITY(state, { quantity, uuid }) {
    let addonExtraIndex = state.addon_extras.findIndex((item) => item.uuid === uuid);

    if (addonExtraIndex === -1) {
      return;
    }

    const extra = state.addon_extras[addonExtraIndex];

    // Limit quantity to the number of people on trip
    quantity = Math.min(quantity, state.peopleCount);

    let updatedExtra = Object.assign({}, extra, { quantity });

    state.addon_extras = [
      ...state.addon_extras.slice(0, addonExtraIndex),
      updatedExtra,
      ...state.addon_extras.slice(addonExtraIndex + 1, state.addon_extras.length),
    ];
  },

  UPDATE_LOADING(state, loading) {
    state.loading = loading;
  },

  UPDATE_ERROR(state, error) {
    state.error = error;
  },

  UPDATE_ADVENTURE(state, adventure) {
    state.adventure = adventure;
  },

  UPDATE_PEOPLE_COUNT(state, count) {
    state.peopleCount = count;
  },
};

const actions = {
  GET_ADVENTURE_DATA({ commit }, id) {
    commit('UPDATE_LOADING', true);
    commit('UPDATE_ERROR', null);

    axios
      .get(`/json/adventures/${id}?with=addons,addons.extras,extras,departures,departures.extras`)
      .then((response) => {
        if (response.status === 200) {
          commit('UPDATE_ADVENTURE', response.data.adventure);
        } else {
          throw 'There was a problem.';
        }
      })
      .catch((error) => {
        console.log(error);
        commit('UPDATE_ERROR', error);
      })
      .then(() => {
        commit('UPDATE_LOADING', false);
      });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
